// Per-user visibility preferences for the Quick Assessor sidebar editor. Lets
// each user hide editor fields they don't need (e.g. tax exemption, owner
// mailing) from their own view. Stored in localStorage (per user / per browser),
// applied across all CIDs; nothing is hidden by default. Every access is wrapped
// so private-mode / quota / SSR failures degrade to a no-op instead of throwing.

const HIDDEN_FIELDS_KEY = "qa.editorHiddenFields";

// The editor fields a user may hide, in the order they appear in the editor.
// `key` is the stable id persisted in localStorage; `label` is the menu text.
// lookupTool gates each field's render on these keys, so keep them in sync.
export const EDITOR_FIELDS = [
  { key: "filters", label: "Filters" },
  { key: "marker", label: "Marker color" },
  { key: "ownerName", label: "Owner name" },
  { key: "ownerMailing", label: "Owner mailing address" },
  { key: "buildingAddress", label: "Building address" },
  { key: "map", label: "Map" },
  { key: "valuation", label: "Valuation & tax exemption" },
  { key: "propertyDesc", label: "Property description" },
  { key: "customFields", label: "Custom fields" },
  { key: "contacts", label: "Contacts" },
];

const VALID_KEYS = new Set(EDITOR_FIELDS.map((f) => f.key));

const safeGet = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore — storage unavailable */
  }
};

// The hidden field keys, filtered to keys the editor still knows about (so a
// renamed/removed field can't strand a stale key that hides nothing).
export const readHiddenFields = () => {
  const raw = safeGet(HIDDEN_FIELDS_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((k) => VALID_KEYS.has(k)) : [];
  } catch {
    return [];
  }
};

// Persists the hidden field keys (deduped + validated) and returns the saved
// array so callers can use it as the next state.
export const writeHiddenFields = (keys) => {
  const unique = Array.from(
    new Set((keys || []).filter((k) => VALID_KEYS.has(k)))
  );
  safeSet(HIDDEN_FIELDS_KEY, JSON.stringify(unique));
  return unique;
};
